/**
 * 头像裁切的取景（Figma `102:7930` / `102:7971`）。
 *
 * 状态记在导出正方形的坐标系里（边长 {@link AVATAR_CROP_SIZE}，原点在正方形中心）。
 * 线性部分只含 90° 旋转和水平翻转，所以画面在这个坐标系里始终是轴对齐矩形。
 * 缩放倍数相对「铺满正方形」单独记账，范围 1–3，不从矩阵里反推。
 *
 * 左转和水平翻转按点击顺序左乘到当前朝向上（绕正方形中心）。二者不对易。
 * 平移和缩小之后把中心夹回「正方形里全是画面」的范围。
 */

export const AVATAR_CROP_SIZE = 512;
export const AVATAR_MIN_ZOOM = 1;
export const AVATAR_MAX_ZOOM = 3;

/** 把图片中心局部坐标映到「未缩放」的源像素轴上。行列式为 ±1 的置换。 */
export type AvatarLinear = {
  a: number;
  b: number;
  c: number;
  d: number;
};

export type AvatarView = {
  linear: AvatarLinear;
  /** 相对铺满的倍数，1 是最小，3 是最大。 */
  zoom: number;
  /** 图片中心相对裁切正方形中心的偏移，单位是裁切像素。 */
  panX: number;
  panY: number;
};

export type AvatarMatrix = {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
};

const IDENTITY: AvatarLinear = { a: 1, b: 0, c: 0, d: 1 };

export function initialAvatarView(): AvatarView {
  return { linear: IDENTITY, zoom: AVATAR_MIN_ZOOM, panX: 0, panY: 0 };
}

/** 源图像素到裁切像素的铺满比例。方向交换不改变这个值。 */
export function coverScale(
  imageWidth: number,
  imageHeight: number,
  crop = AVATAR_CROP_SIZE,
): number {
  return Math.max(crop / imageWidth, crop / imageHeight);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function halfExtents(
  view: AvatarView,
  imageWidth: number,
  imageHeight: number,
  crop: number,
): { halfW: number; halfH: number; scale: number } {
  const scale = coverScale(imageWidth, imageHeight, crop) * view.zoom;
  const { a, b, c, d } = view.linear;
  return {
    scale,
    halfW: (scale * (Math.abs(a) * imageWidth + Math.abs(c) * imageHeight)) / 2,
    halfH: (scale * (Math.abs(b) * imageWidth + Math.abs(d) * imageHeight)) / 2,
  };
}

/** 把图片中心夹进仍能盖住整个裁切正方形的范围。 */
export function clampAvatarPan(
  view: AvatarView,
  imageWidth: number,
  imageHeight: number,
  crop = AVATAR_CROP_SIZE,
): AvatarView {
  const { halfW, halfH } = halfExtents(view, imageWidth, imageHeight, crop);
  const limitX = Math.max(0, halfW - crop / 2);
  const limitY = Math.max(0, halfH - crop / 2);
  const panX = clamp(view.panX, -limitX, limitX);
  const panY = clamp(view.panY, -limitY, limitY);
  if (panX === view.panX && panY === view.panY) {
    return view;
  }
  return { ...view, panX, panY };
}

export function panAvatar(
  view: AvatarView,
  dx: number,
  dy: number,
  imageWidth: number,
  imageHeight: number,
  crop = AVATAR_CROP_SIZE,
): AvatarView {
  return clampAvatarPan(
    { ...view, panX: view.panX + dx, panY: view.panY + dy },
    imageWidth,
    imageHeight,
    crop,
  );
}

/**
 * 把缩放改到 `nextZoom`，并让裁切坐标 `(px, py)`（相对中心）底下的那一点留在原地。
 * 滑条传 `(0, 0)`，滚轮传指针相对圆环中心的位置。
 */
export function zoomAvatar(
  view: AvatarView,
  nextZoom: number,
  px: number,
  py: number,
  imageWidth: number,
  imageHeight: number,
  crop = AVATAR_CROP_SIZE,
): AvatarView {
  const zoom = clamp(nextZoom, AVATAR_MIN_ZOOM, AVATAR_MAX_ZOOM);
  const cover = coverScale(imageWidth, imageHeight, crop);
  const prev = cover * view.zoom;
  const next = cover * zoom;
  const ratio = prev === 0 ? 1 : next / prev;
  return clampAvatarPan(
    {
      ...view,
      zoom,
      panX: ratio * view.panX + px * (1 - ratio),
      panY: ratio * view.panY + py * (1 - ratio),
    },
    imageWidth,
    imageHeight,
    crop,
  );
}

function compose(op: AvatarLinear, linear: AvatarLinear): AvatarLinear {
  return {
    a: op.a * linear.a + op.c * linear.b,
    b: op.b * linear.a + op.d * linear.b,
    c: op.a * linear.c + op.c * linear.d,
    d: op.b * linear.c + op.d * linear.d,
  };
}

/** 屏幕左转 90°：顶边转到左边。绕裁切中心。 */
const ROTATE_LEFT: AvatarLinear = { a: 0, b: -1, c: 1, d: 0 };

/** 沿裁切正方形的竖轴做水平翻转。 */
const FLIP_X: AvatarLinear = { a: -1, b: 0, c: 0, d: 1 };

function applyAroundCenter(
  view: AvatarView,
  op: AvatarLinear,
  imageWidth: number,
  imageHeight: number,
  crop: number,
): AvatarView {
  return clampAvatarPan(
    {
      ...view,
      linear: compose(op, view.linear),
      panX: op.a * view.panX + op.c * view.panY,
      panY: op.b * view.panX + op.d * view.panY,
    },
    imageWidth,
    imageHeight,
    crop,
  );
}

export function rotateAvatarLeft(
  view: AvatarView,
  imageWidth: number,
  imageHeight: number,
  crop = AVATAR_CROP_SIZE,
): AvatarView {
  return applyAroundCenter(view, ROTATE_LEFT, imageWidth, imageHeight, crop);
}

export function flipAvatarHorizontal(
  view: AvatarView,
  imageWidth: number,
  imageHeight: number,
  crop = AVATAR_CROP_SIZE,
): AvatarView {
  return applyAroundCenter(view, FLIP_X, imageWidth, imageHeight, crop);
}

/**
 * 把源图像素 `(sx, sy)`（左上为原点）映到裁切正方形像素（左上为原点）。
 * 给 canvas `setTransform` 和预览 `<img>` 共用。
 */
export function avatarMatrix(
  view: AvatarView,
  imageWidth: number,
  imageHeight: number,
  crop = AVATAR_CROP_SIZE,
): AvatarMatrix {
  const { scale } = halfExtents(view, imageWidth, imageHeight, crop);
  const { a, b, c, d } = view.linear;
  const center = crop / 2;
  return {
    a: scale * a,
    b: scale * b,
    c: scale * c,
    d: scale * d,
    e:
      view.panX +
      center -
      scale * (a * (imageWidth / 2) + c * (imageHeight / 2)),
    f:
      view.panY +
      center -
      scale * (b * (imageWidth / 2) + d * (imageHeight / 2)),
  };
}

/** 预览把 512 坐标系缩进屏幕上的裁切框。 */
export function avatarCssMatrix(
  view: AvatarView,
  imageWidth: number,
  imageHeight: number,
  displaySize: number,
): string {
  const fitted = avatarMatrix(view, imageWidth, imageHeight, AVATAR_CROP_SIZE);
  const scale = displaySize / AVATAR_CROP_SIZE;
  const m = {
    a: fitted.a * scale,
    b: fitted.b * scale,
    c: fitted.c * scale,
    d: fitted.d * scale,
    e: fitted.e * scale,
    f: fitted.f * scale,
  };
  return `matrix(${m.a}, ${m.b}, ${m.c}, ${m.d}, ${m.e}, ${m.f})`;
}

/** 滚轮向上（`deltaY < 0`）放大。一格大约 1.15 倍。 */
export function zoomFactorFromWheel(deltaY: number): number {
  return Math.exp(-deltaY * 0.0014);
}
